/**
 * Chat service — REST + SSE wrapper.
 * Keep it tiny; everything else lives in the React components.
 */
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const authHeaders = () => {
  const t = localStorage.getItem('token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const chatService = {
  /** Idempotent: returns the existing thread for (current user, property) or creates one. */
  createOrGet: async (propertyId) => {
    const r = await axios.post(`${API}/api/chats`,
      { property_id: propertyId }, { headers: authHeaders() });
    return r.data;
  },

  list: async () => {
    const r = await axios.get(`${API}/api/chats`, { headers: authHeaders() });
    return r.data;
  },

  get: async (chatId) => {
    const r = await axios.get(`${API}/api/chats/${chatId}`, { headers: authHeaders() });
    return r.data;
  },

  send: async (chatId, body) => {
    const r = await axios.post(`${API}/api/chats/${chatId}/messages`,
      { body }, { headers: authHeaders() });
    return r.data;
  },

  fetchHistory: async (chatId, beforeMessageId, limit = 30) => {
    const params = { limit };
    if (beforeMessageId) params.before = beforeMessageId;
    const r = await axios.get(`${API}/api/chats/${chatId}/messages`,
      { params, headers: authHeaders() });
    return r.data;
  },

  markRead: async (chatId) => {
    await axios.post(`${API}/api/chats/${chatId}/read`, {}, { headers: authHeaders() });
  },

  deleteMessage: async (chatId, messageId) => {
    await axios.delete(`${API}/api/chats/${chatId}/messages/${messageId}`,
      { headers: authHeaders() });
  },

  /**
   * Open a Server-Sent Events stream for live updates.
   * Returns an EventSource-like object with `.close()`.
   *
   * Browser EventSource can't set Authorization headers, so we pass the token as
   * a query param. Backend reads it via `get_current_user` (which already
   * accepts Bearer tokens — we send the token in a query as a fallback).
   *
   * For simplicity in MVP we use polling as a tiny fallback if EventSource breaks.
   */
  openStream: (chatId, { onMessage, onRead, onDelete } = {}) => {
    const token = localStorage.getItem('token');
    if (!token) return { close: () => {} };
    // EventSource doesn't support Authorization header — fall back to polling.
    // Simple, robust, no token-in-url leakage.
    let stopped = false;
    let lastSeen = Date.now();
    const tick = async () => {
      if (stopped) return;
      try {
        const r = await axios.get(`${API}/api/chats/${chatId}/messages`,
          { params: { limit: 20 }, headers: authHeaders() });
        const msgs = r.data?.messages || [];
        for (const m of msgs) {
          const ts = new Date(m.sent_at).getTime();
          if (ts > lastSeen && m.deleted_at) {
            onDelete?.({ id: m.id });
          } else if (ts > lastSeen) {
            onMessage?.(m);
          }
        }
        if (msgs.length) {
          lastSeen = Math.max(lastSeen, ...msgs.map((m) => new Date(m.sent_at).getTime()));
        }
      } catch (_e) { /* swallow */ }
      if (!stopped) setTimeout(tick, 3500);
    };
    tick();
    return { close: () => { stopped = true; } };
  },
};

export default chatService;
